import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useRef, useMemo, useEffect, useLayoutEffect, useState } from 'react';
import Home from '../pages/Home';
import Products from '../pages/Products';
import ProductDetail from '../pages/ProductDetail';
import ProductForm from '../pages/ProductForm';
import StockIn from '../pages/StockIn';
import Sales from '../pages/Sales';
import Orders from '../pages/Orders';
import OrderDetail from '../pages/OrderDetail';
import Scan from '../pages/Scan';
import StockCheck from '../pages/StockCheck';
import Statistics from '../pages/Statistics';
import ChinaOrders from '../pages/ChinaOrders';

// Порядок вкладок в BottomBar (слева направо)
const bottomBarTabs = [
  '/products',
  '/stock-in',
  '/china-orders',
  '/orders',
  '/statistics',
];

// Функция для получения базового пути (без параметров)
const getBasePath = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return '/';
  return '/' + parts[0];
};

// Функция для получения индекса пути в BottomBar
const getTabIndex = (pathname) => {
  const basePath = getBasePath(pathname);
  for (let i = 0; i < bottomBarTabs.length; i++) {
    if (basePath === bottomBarTabs[i] || pathname.startsWith(bottomBarTabs[i] + '/')) {
      return i;
    }
  }
  return -1;
};

// Функция для определения направления анимации
const getDirection = (currentPath, prevPath) => {
  // Если путь не изменился, нет анимации
  if (currentPath === prevPath) {
    return 0;
  }

  const currentIndex = getTabIndex(currentPath);
  const prevIndex = getTabIndex(prevPath);
  const currentBasePath = getBasePath(currentPath);
  const prevBasePath = getBasePath(prevPath);

  // Случай 1: Оба пути в BottomBar
  if (currentIndex !== -1 && prevIndex !== -1) {
    // Если это разные вкладки, направление зависит от их позиций
    if (currentIndex !== prevIndex) {
      return currentIndex > prevIndex ? 1 : -1;
    }
    // Если это одна и та же вкладка (например, /products -> /products), нет анимации
    return 0;
  }

  // Случай 2: Текущий путь в BottomBar, предыдущий - детальная страница
  if (currentIndex !== -1 && prevIndex === -1) {
    // Если возвращаемся к базовой странице из детальной
    if (prevPath.startsWith(currentBasePath + '/')) {
      return -1; // Возврат назад (влево)
    }
    // Если переходим с другой детальной страницы, определяем по базовым путям
    const prevBaseIndex = bottomBarTabs.indexOf(prevBasePath);
    if (prevBaseIndex !== -1 && currentIndex !== prevBaseIndex) {
      return currentIndex > prevBaseIndex ? 1 : -1;
    }
    return 0;
  }

  // Случай 3: Предыдущий путь в BottomBar, текущий - детальная страница
  if (currentIndex === -1 && prevIndex !== -1) {
    // Если переходим в детальную страницу из базовой
    if (currentPath.startsWith(prevBasePath + '/')) {
      return 1; // Переход вперед (вправо)
    }
    // Если переходим в детальную страницу другой вкладки
    const currentBaseIndex = bottomBarTabs.indexOf(currentBasePath);
    if (currentBaseIndex !== -1 && currentBaseIndex !== prevIndex) {
      return currentBaseIndex > prevIndex ? 1 : -1;
    }
    return 0;
  }

  // Случай 4: Оба пути не в BottomBar (детальные страницы)
  if (currentIndex === -1 && prevIndex === -1) {
    // Если это детальные страницы одной базовой вкладки
    if (currentBasePath === prevBasePath) {
      const currentDepth = currentPath.split('/').filter(Boolean).length;
      const prevDepth = prevPath.split('/').filter(Boolean).length;
      // Если глубина увеличилась - вперед (вправо), уменьшилась - назад (влево)
      return currentDepth > prevDepth ? 1 : currentDepth < prevDepth ? -1 : 0;
    }
    // Если это детальные страницы разных базовых вкладок
    const currentBaseIndex = bottomBarTabs.indexOf(currentBasePath);
    const prevBaseIndex = bottomBarTabs.indexOf(prevBasePath);
    if (currentBaseIndex !== -1 && prevBaseIndex !== -1) {
      return currentBaseIndex > prevBaseIndex ? 1 : -1;
    }
  }

  return 0;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathRef = useRef(location.pathname);
  const exitDirectionRef = useRef(0); // Сохраняем направление выхода для старой страницы
  const prevExitDirectionRef = useRef(0); // Сохраняем предыдущее направление выхода
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const touchEndRef = useRef({ x: 0, y: 0, time: 0 });
  const isSwipingRef = useRef(false);

  // Вычисляем направление анимации для новой страницы
  const direction = useMemo(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;
    
    // Если путь не изменился, возвращаем предыдущее направление
    if (currentPath === prevPath) {
      return 0;
    }
    
    const dir = getDirection(currentPath, prevPath);
    
    // Сохраняем текущее направление выхода в предыдущее (для старой страницы)
    prevExitDirectionRef.current = exitDirectionRef.current;
    
    // Сохраняем новое направление выхода для следующей страницы
    // Обе страницы двигаются в одном направлении, поэтому exitDirection = direction
    exitDirectionRef.current = dir;
    
    return dir;
  }, [location.pathname]);

  // Обработка свайпов
  useEffect(() => {
    // Проверяем, что элемент не является интерактивным
    const isInteractiveElement = (element) => {
      if (!element) return false;
      
      const tagName = element.tagName?.toLowerCase();
      const interactiveTags = ['input', 'textarea', 'button', 'select', 'a'];
      if (interactiveTags.includes(tagName)) return true;
      
      // Проверяем, является ли элемент scrollable
      const style = window.getComputedStyle(element);
      const isScrollable = 
        element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth;
      
      if (isScrollable && (style.overflow === 'auto' || style.overflow === 'scroll' || style.overflowY === 'auto' || style.overflowY === 'scroll')) {
        return true;
      }
      
      // Проверяем родительские элементы
      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        if (interactiveTags.includes(parent.tagName?.toLowerCase())) {
          return true;
        }
        parent = parent.parentElement;
      }
      
      return false;
    };

    const handleTouchStart = (e) => {
      const target = e.target;
      
      // Не обрабатываем свайп, если начали на интерактивном элементе
      if (isInteractiveElement(target)) {
        return;
      }
      
      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
        target: target,
      };
      isSwipingRef.current = false;
    };

    const handleTouchMove = (e) => {
      if (!touchStartRef.current.x) return;
      
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
      
      // Определяем, что это горизонтальный свайп
      if (deltaX > 10 && deltaX > deltaY * 1.5) {
        isSwipingRef.current = true;
        // Предотвращаем скролл страницы во время свайпа только если это действительно свайп
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e) => {
      if (!touchStartRef.current.x || !isSwipingRef.current) {
        touchStartRef.current = { x: 0, y: 0, time: 0, target: null };
        return;
      }

      // Проверяем, что не закончили на интерактивном элементе
      const endTarget = e.changedTouches[0] ? document.elementFromPoint(
        e.changedTouches[0].clientX,
        e.changedTouches[0].clientY
      ) : null;
      
      if (isInteractiveElement(endTarget)) {
        touchStartRef.current = { x: 0, y: 0, time: 0, target: null };
        isSwipingRef.current = false;
        return;
      }

      const touch = e.changedTouches[0];
      touchEndRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };

      const deltaX = touchEndRef.current.x - touchStartRef.current.x;
      const deltaY = Math.abs(touchEndRef.current.y - touchStartRef.current.y);
      const deltaTime = touchEndRef.current.time - touchStartRef.current.time;
      const minSwipeDistance = 50; // Минимальное расстояние для свайпа
      const maxSwipeTime = 300; // Максимальное время для свайпа (мс)

      // Проверяем, что это горизонтальный свайп достаточной длины и быстрый
      if (
        Math.abs(deltaX) > minSwipeDistance &&
        Math.abs(deltaX) > deltaY * 1.5 &&
        deltaTime < maxSwipeTime
      ) {
        const currentIndex = getTabIndex(location.pathname);
        
        // Свайп работает только для страниц из BottomBar
        if (currentIndex !== -1) {
          if (deltaX > 0) {
            // Свайп вправо - переходим на предыдущую страницу (влево по навбару)
            if (currentIndex > 0) {
              navigate(bottomBarTabs[currentIndex - 1]);
            }
          } else {
            // Свайп влево - переходим на следующую страницу (вправо по навбару)
            if (currentIndex < bottomBarTabs.length - 1) {
              navigate(bottomBarTabs[currentIndex + 1]);
            }
          }
        }
      }

      touchStartRef.current = { x: 0, y: 0, time: 0, target: null };
      isSwipingRef.current = false;
    };

    const container = document.body;
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [location.pathname, navigate]);

  // Обновляем предыдущий путь ПОСЛЕ вычисления направления
  useEffect(() => {
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  // Вычисляем варианты анимации динамически
  const pageVariants = useMemo(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;
    
    // Варианты для входа (новая страница)
    // При переходе вправо (direction = 1) новая страница приходит справа и движется влево
    // При переходе влево (direction = -1) новая страница приходит слева и движется вправо
    let enterInitial, enterIn;
    if (direction === 1) {
      // Переход вправо: новая страница приходит справа (движется влево)
      enterInitial = { x: '100%' };
      enterIn = { x: 0 };
    } else if (direction === -1) {
      // Переход влево: новая страница приходит слева (движется вправо)
      enterInitial = { x: '-100%' };
      enterIn = { x: 0 };
    } else {
      enterInitial = { opacity: 0 };
      enterIn = { opacity: 1 };
    }
    
    // Варианты для выхода (старая страница)
    // Обе страницы двигаются в одном направлении, поэтому используем то же направление
    // Но для старой страницы нужно использовать направление, вычисленное ДО изменения location
    // Используем prevExitDirectionRef, который был установлен в предыдущем рендере
    // Если это первый переход, используем текущее direction
    const exitDir = prevExitDirectionRef.current !== 0 ? prevExitDirectionRef.current : direction;
    let exitOut;
    if (exitDir === 1) {
      // Переход вправо: старая страница уходит влево (вместе с новой)
      exitOut = { x: '-100%' };
    } else if (exitDir === -1) {
      // Переход влево: старая страница уходит вправо (вместе с новой)
      exitOut = { x: '100%' };
    } else {
      exitOut = { opacity: 0 };
    }
    
    return {
      initial: enterInitial,
      in: enterIn,
      out: exitOut,
    };
  }, [direction, location.pathname]); // location.pathname для пересчета при изменении

  const pageTransition = {
    type: 'tween',
    ease: [0.25, 0.1, 0.25, 1],
    duration: 0.25,
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
        transition={pageTransition}
        className="w-full h-full"
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/products/new" element={<ProductForm />} />
          <Route path="/products/edit/:id" element={<ProductForm />} />
          <Route path="/stock-in" element={<StockIn />} />
          <Route path="/sales" element={<Sales />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/china-orders" element={<ChinaOrders />} />
          <Route path="/scan" element={<Scan />} />
          <Route path="/stock-check" element={<StockCheck />} />
          <Route path="/statistics" element={<Statistics />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

export default AnimatedRoutes;

